import { useRecordContext, useTranslate } from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { DateField } from "@/components/admin/date-field";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import { ReferenceField } from "@/components/admin/reference-field";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useGetIdentity } from "ra-core";
import { Link } from "react-router";

import { TopToolbar } from "../layout/TopToolbar";
import type { Campaign } from "../types";
import { useIsCampaignAdmin } from "./useIsCampaignAdmin";
import { useCampaignStatusChoices } from "./useCampaignStatusChoices";

const CampaignProgressCell = () => {
  const record = useRecordContext<Campaign>();
  const isAdmin = useIsCampaignAdmin();
  const translate = useTranslate();
  if (!record || !isAdmin) return null;
  const total = record.total_tasks ?? 0;
  if (total === 0) return <span className="text-muted-foreground text-sm">—</span>;
  const pct = Math.round(((record.completed_tasks ?? 0) / total) * 100);
  const overdue = record.overdue_tasks ?? 0;
  return (
    <div className="flex flex-col gap-1 min-w-[120px]">
      <div className="flex items-center gap-2 text-sm">
        <Progress value={pct} className="h-2 flex-1" />
        <span>{pct}%</span>
      </div>
      {overdue > 0 && (
        <Badge variant="destructive" className="w-fit">
          {translate("resources.campaigns.progress.overdue_count", {
            count: overdue,
            _: `${overdue} overdue`,
          })}
        </Badge>
      )}
    </div>
  );
};

const CampaignListActions = () => {
  const isAdmin = useIsCampaignAdmin();
  if (!isAdmin) return null;
  return (
    <TopToolbar>
      <CreateButton />
    </TopToolbar>
  );
};

export const CampaignList = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const isAdmin = useIsCampaignAdmin();
  const statusChoices = useCampaignStatusChoices();

  if (!identity) return null;

  const campaignFilters = [
    <SearchInput source="q" alwaysOn />,
    <SelectInput source="status" choices={statusChoices} alwaysOn />,
  ];

  return (
    <List
      title={translate("resources.campaigns.name", { smart_count: 2, _: "Campaigns" })}
      perPage={25}
      sort={{ field: "created_at", order: "DESC" }}
      filters={campaignFilters}
      actions={<CampaignListActions />}
      pagination={<ListPagination />}
    >
      <DataTable rowClick="show">
        <DataTable.Col source="name" />
        <DataTable.Col source="status" />
        <DataTable.Col source="product_name" />
        <DataTable.Col source="start_date">
          <DateField source="start_date" />
        </DataTable.Col>
        <DataTable.Col source="end_date">
          <DateField source="end_date" />
        </DataTable.Col>
        {isAdmin && (
          <DataTable.Col
            label={translate("resources.campaigns.progress.label", {
              _: "Progress",
            })}
          >
            <CampaignProgressCell />
          </DataTable.Col>
        )}
        <DataTable.Col source="created_by">
          <ReferenceField source="created_by" reference="sales" link={false} />
        </DataTable.Col>
      </DataTable>
    </List>
  );
};

export const CampaignNameLink = () => {
  const record = useRecordContext<Campaign>();
  if (!record) return null;
  return (
    <Link to={`/campaigns/${record.id}/show`} className="font-medium hover:underline">
      {record.name}
    </Link>
  );
};
